import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';

export interface Permission {
  resource: string;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  scope?: string;
}

interface PermissionMatrixProps {
  permissions: Permission[];
  onChange: (permissions: Permission[]) => void;
  disabled?: boolean;
  excludeResources?: string[]; // Resources to exclude from the matrix (e.g., ['permissions'] for custom roles)
}

const RESOURCES = [
  { key: 'incidents', label: 'Incidents' },
  { key: 'problems', label: 'Problems' },
  { key: 'changes', label: 'Changes' },
  { key: 'cases', label: 'Cases' },
  { key: 'alerts', label: 'Alerts' },
  { key: 'surveys', label: 'Surveys' },
  { key: 'survey_templates', label: 'Survey Templates' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'service_requests', label: 'Service Requests' },
  { key: 'knowledge', label: 'Knowledge Articles' },
  { key: 'knowledge_base', label: 'Knowledge Base' },
  { key: 'groups', label: 'Groups' },
  { key: 'users', label: 'Users' },
  { key: 'customers', label: 'Customers' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'permissions', label: 'Permissions' },
  { key: 'variable-templates', label: 'Variables' },
  { key: 'engagements', label: 'Engagements' },
  { key: 'service_plans', label: 'Service Plans' },
  { key: 'resources', label: 'Resources' },
  { key: 'offers', label: 'Offers' },
  { key: 'workflows', label: 'Workflows' },
  { key: 'environments', label: 'Environments' },
  { key: 'requests', label: 'Requested Items' },
  { key: 'pipelines', label: 'Pipelines' },
  { key: 'sla_conditions', label: 'SLA Conditions' },
  { key: 'email_template', label: 'Email Templates' },
  { key: 'notification_rule', label: 'Notification Rules' },
  { key: 'notification_log', label: 'Notification Logs' },
  { key: 'integration_logs', label: 'Integration Logs' },
  { key: 'case_suppression', label: 'Case Suppression' },
  { key: 'cab_definitions', label: 'CAB Definitions' },
  { key: 'cab_workbench', label: 'CAB WorkBench' },
  { key: 'time_entries', label: 'Time Entries' },
  { key: 'bandwidth', label: 'Bandwidth' },
  { key: 'kpi_dashboard', label: 'KPI Dashboard' },
  { key: 'billing', label: 'Billing' }
];

const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  permissions,
  onChange,
  disabled = false,
  excludeResources = []
}) => {
  const [localPermissions, setLocalPermissions] = useState<Permission[]>([]);

  useEffect(() => {
    // Filter out excluded resources (e.g., 'permissions' for custom roles)
    const filteredResources = RESOURCES.filter(
      resource => !excludeResources.includes(resource.key)
    );

    // Initialize permissions for filtered resources
    const initialPermissions = filteredResources.map(resource => {
      const existing = permissions.find(p => p.resource === resource.key);
      return existing || {
        resource: resource.key,
        create: false,
        read: false,
        update: false,
        delete: false
      };
    });
    setLocalPermissions(initialPermissions);
  }, [permissions, excludeResources]);

  const handlePermissionChange = (
    resourceKey: string,
    action: 'create' | 'read' | 'update' | 'delete',
    value: boolean
  ) => {
    const updatedPermissions = localPermissions.map(perm => {
      if (perm.resource === resourceKey) {
        return { ...perm, [action]: value };
      }
      return perm;
    });
    setLocalPermissions(updatedPermissions);
    onChange(updatedPermissions);
  };

  const handleBulkAction = (action: 'create' | 'read' | 'update' | 'delete', value: boolean) => {
    const updatedPermissions = localPermissions.map(perm => ({
      ...perm,
      [action]: value
    }));
    setLocalPermissions(updatedPermissions);
    onChange(updatedPermissions);
  };

  const handleGrantAll = () => {
    const updatedPermissions = localPermissions.map(perm => ({
      ...perm,
      create: true,
      read: true,
      update: true,
      delete: true
    }));
    setLocalPermissions(updatedPermissions);
    onChange(updatedPermissions);
  };

  const handleDenyAll = () => {
    const updatedPermissions = localPermissions.map(perm => ({
      ...perm,
      create: false,
      read: false,
      update: false,
      delete: false
    }));
    setLocalPermissions(updatedPermissions);
    onChange(updatedPermissions);
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      <div className="flex items-center justify-end space-x-2">
        <button
          type="button"
          onClick={handleGrantAll}
          disabled={disabled}
          className="px-3 py-1 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-300"
        >
          Grant All
        </button>
        <button
          type="button"
          onClick={handleDenyAll}
          disabled={disabled}
          className="px-3 py-1 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-300"
        >
          Deny All
        </button>
      </div>

      {/* Permission Matrix */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Resource
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex flex-col items-center">
                  <span>Create</span>
                  <input
                    type="checkbox"
                    className="mt-1"
                    onChange={(e) => handleBulkAction('create', e.target.checked)}
                    disabled={disabled}
                  />
                </div>
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex flex-col items-center">
                  <span>Read</span>
                  <input
                    type="checkbox"
                    className="mt-1"
                    onChange={(e) => handleBulkAction('read', e.target.checked)}
                    disabled={disabled}
                  />
                </div>
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex flex-col items-center">
                  <span>Update</span>
                  <input
                    type="checkbox"
                    className="mt-1"
                    onChange={(e) => handleBulkAction('update', e.target.checked)}
                    disabled={disabled}
                  />
                </div>
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex flex-col items-center">
                  <span>Delete</span>
                  <input
                    type="checkbox"
                    className="mt-1"
                    onChange={(e) => handleBulkAction('delete', e.target.checked)}
                    disabled={disabled}
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {localPermissions.map((permission) => {
              const resource = RESOURCES.find(r => r.key === permission.resource);
              if (!resource) return null;

              return (
                <tr key={permission.resource}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {resource.label}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <Switch
                      checked={permission.create}
                      onChange={(checked) => handlePermissionChange(resource.key, 'create', checked)}
                      disabled={disabled}
                      className={`${
                        permission.create ? 'bg-primary-600' : 'bg-gray-200'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          permission.create ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <Switch
                      checked={permission.read}
                      onChange={(checked) => handlePermissionChange(resource.key, 'read', checked)}
                      disabled={disabled}
                      className={`${
                        permission.read ? 'bg-primary-600' : 'bg-gray-200'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          permission.read ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <Switch
                      checked={permission.update}
                      onChange={(checked) => handlePermissionChange(resource.key, 'update', checked)}
                      disabled={disabled}
                      className={`${
                        permission.update ? 'bg-primary-600' : 'bg-gray-200'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          permission.update ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <Switch
                      checked={permission.delete}
                      onChange={(checked) => handlePermissionChange(resource.key, 'delete', checked)}
                      disabled={disabled}
                      className={`${
                        permission.delete ? 'bg-primary-600' : 'bg-gray-200'
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2`}
                    >
                      <span
                        className={`${
                          permission.delete ? 'translate-x-6' : 'translate-x-1'
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PermissionMatrix;